import { useDispatch } from "react-redux";
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardActions,
  IconButton,
  Tabs,
  Tab
} from "@mui/material";
import { Delete } from "@mui/icons-material";

import {
  getAssessmentTemplateImages,
  deleteAssessmentTemplateImage
} from "../../../../actions/assessments";
import { IThunkDispatch } from "../../../../store";
import Dialog from "../../../../components/Dialog/Dialog";
import useCurrentResourceCentre from "../../../../hooks/useCurrentResourceCentre";
import { AssessmentTemplateImageUpload } from "./AssessmentTemplateImageUpload";

// assessment template image categories
export const AssessmentTemplateImageCategory = {
  GENERAL: "general",
  DENTAL: "dental",
  EYE: "eye",
  DERMA: "derma"
} as const;

export const AssessmentTemplateImageCategories = [
  { label: "General", value: AssessmentTemplateImageCategory.GENERAL },
  { label: "Dental", value: AssessmentTemplateImageCategory.DENTAL },
  { label: "Derma", value: AssessmentTemplateImageCategory.DERMA },
  { label: "Eye", value: AssessmentTemplateImageCategory.EYE }
];

export function AssessmentTemplateImagesSetting(): React.ReactElement {
  const dispatch = useDispatch<IThunkDispatch>();
  const currentResourceCentre = useCurrentResourceCentre();

  // state
  const [selectedCategory, setSelectedCategory] = useState<string>(
    AssessmentTemplateImageCategory.DENTAL
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<{ open: boolean; id: number | null }>({
    open: false,
    id: null
  });

  // variables
  const templateImages = currentResourceCentre?.assessmentTemplateImages || [];
  const imagesForCategory = templateImages.filter((img) => img.entityType === selectedCategory);

  // use effect
  useEffect(() => {
    dispatch(getAssessmentTemplateImages());
  }, [dispatch]);

  // helpers functions
  const handleDelete = async () => {
    if (!deleteDialogOpen.id) return;

    try {
      await dispatch(deleteAssessmentTemplateImage(deleteDialogOpen.id));
    } finally {
      setDeleteDialogOpen({ open: false, id: null });
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Assessment Template Images
      </Typography>

      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <Tabs value={selectedCategory} onChange={(_e, newValue) => setSelectedCategory(newValue)}>
          {AssessmentTemplateImageCategories.filter(
            (c) => c.value !== AssessmentTemplateImageCategory.GENERAL
          ).map((c) => (
            <Tab key={c.value} label={c.label} value={c.value} />
          ))}
        </Tabs>

        {/* upload image button moved to reusable component */}
        <Box sx={{ mb: 1 }}>
          <AssessmentTemplateImageUpload category={selectedCategory} />
        </Box>
      </Box>

      {/* images list section */}
      <Grid container spacing={2}>
        {imagesForCategory.map((img) => (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={img.id}>
            <Card>
              <CardMedia
                component="img"
                height="140"
                image={img.s3ResourceURL}
                alt={img.fileName}
                sx={{ objectFit: "contain", bgcolor: "#f5f5f5" }}
              />
              <CardActions sx={{ justifyContent: "flex-end" }}>
                <IconButton
                  color="error"
                  onClick={() => setDeleteDialogOpen({ open: true, id: img.id })}
                >
                  <Delete />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* empty state */}
      {imagesForCategory.length === 0 && (
        <Box
          sx={(theme) => ({
            minHeight: "250px",
            border: "2px dashed",
            borderColor: theme.palette.divider,
            borderRadius: "5px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          })}
        >
          <Typography>No images found in this category.</Typography>
        </Box>
      )}

      {deleteDialogOpen.open && (
        <Dialog
          title="Delete Image"
          description="Are you sure you want to delete this template image?"
          next={handleDelete}
          cancel={() => setDeleteDialogOpen({ open: false, id: null })}
          readMode={false}
        />
      )}
    </Box>
  );
}
